// TelcoTrack — Workflow: názvy a barvy fází.
//
// Nový model (z Excelu): kódy fází jsou `WORKFLOWID.PHASE.N` a žijí ve vygenerovaných datech
// (`generated-workflows.ts`). Tento soubor drží jen barvy (PHASE_COLORS), normalizaci typu
// a odvozené názvy fází podle kódu.
//
// UI popisky fáze: vždy `resolvePhaseLabel(code)` (nebo `getPhaseName`).
// Navigace / přechody / barvy: `workflow-model.ts` (PHASE_BY_CODE, getPhaseByCode, …).

import { GENERATED_WORKFLOWS } from './generated-workflows';

// =============================================================================
// PHASE COLORS
// =============================================================================

export const PHASE_COLORS = {
  RED: '#dc2626',
  ORANGE: '#f97316',
  YELLOW: '#eab308',
  GREEN: '#16a34a',
  BLUE: '#3b82f6',
  PURPLE: '#9333ea',
  PINK: '#ec4899',
  BEIGE: '#d4a574',
  BLACK: '#000000',
  /** INFO / šedá v tabulkách Barvy. */
  GRAY: '#6b7280',
  BLUE_TRANSPARENT: '#3b82f680',
  GREEN_TRANSPARENT: '#16a34a80',
  /** Oranžová transparentní (50% alpha, stejný vzor jako BLUE_TRANSPARENT). */
  ORANGE_TRANSPARENT: '#f9731680',
} as const;

/** Klíč barvy v `PHASE_COLORS` (zdroj pro vygenerovaná workflow data). */
export type ColorKey = keyof typeof PHASE_COLORS;

// =============================================================================
// PHASE NAMES (odvozené z vygenerovaných dat: kód → frontend název)
// =============================================================================

/** Mapa kód fáze (`WORKFLOWID.PHASE.N`) → český frontend název. */
export const PHASE_NAMES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const wf of GENERATED_WORKFLOWS) {
    for (const phase of wf.phases) {
      map[phase.code] = phase.name;
    }
  }
  return map;
})();

/** Vrací český název fáze podle kódu; při neznámém kódu vrátí kód. */
export function getPhaseName(phaseCode: string): string {
  return PHASE_NAMES[(phaseCode ?? '').trim()] ?? (phaseCode ?? '').trim();
}

/**
 * Kanonický UI popisek workflow fáze z kódu (`WF.PHASE.N`).
 * `objectType` je volitelný jen kvůli čitelnosti volání — název je v lineárním modelu
 * jednoznačně daný kódem fáze (viz `PHASE_NAMES` z Excelu).
 */
export function resolvePhaseLabel(phaseCode: string, _objectType?: string | null): string {
  return getPhaseName(phaseCode);
}

// =============================================================================
// SÉMANTICKÉ NÁZVY FÁZÍ (pro porovnání statusu napříč variantami)
// =============================================================================

/**
 * Kanonické názvy fází podle Výkresy.xlsx. Klíče odpovídají původním sémantickým kódům,
 * aby byla migrace konzumentů čitelná. Status objektu se porovnává podle NÁZVU
 * (`isSamePhase`) — u BUDOVA je varianta (.1/.2/.3) v kódu, ale logika je name-based.
 *
 * ⚠ Některé názvy se opakují napříč typy (`Finální`) a u BUDOVA napříč variantami
 * (`Zafouknutý`, `Nepřipojený`) — porovnávej vždy v kontextu známého typu objektu.
 */
export const PHASE_NAME = {
  VYKOP_NEVYKOPANY: 'Nevykopaný',
  VYKOP_VYKOPANY: 'Vykopaný',
  VYKOP_ZAKOPANY: 'Finální',
  VYKOP_PREDANY: 'Předaný',
  STAVAJICI_STAVAJICI: 'Stávající',
  ROZVADEC_NEZACATE: 'Nezačaté',
  ROZVADEC_POSTAVENY: 'Postavený',
  ROZVADEC_ZAFOUKNUTO_SLOUPEK: 'Zafouknutý',
  /** Konečná lineární svařovací fáze (dříve Provařené / dual milestones). */
  ROZVADEC_PROVARENE: 'Svařený',
  /** @deprecated Milník — už není lineární workflow fáze. */
  ROZVADEC_PROVARENY_PRIVOD: 'Provařený přívod',
  /** @deprecated Milník — už není lineární workflow fáze. */
  ROZVADEC_ZRIZENO: 'Provaření sekundárních OK',
  BUDOVA_NEPRIPOJENY: 'Nepřipojený',
  BUDOVA_ODMITNUTY: 'Zrušený',
  BUDOVA_PRIPOJENE_PLASTY: 'Připojené plasty',
  /** Milník (Výkresy Milníky @ BUDOVA.2 PHASE.1) — už není lineární workflow fáze. */
  BUDOVA_ZRIZENE_VR: 'Zřízené VR',
  BUDOVA_POSTAVENA_BABOVKA: 'Připravená bábovka',
  BUDOVA_ZAFOUKNUTO: 'Zafouknutý',
  BUDOVA_SVARENY: 'Svařený',
  SPOJKOVISTE_NEPRIPOJENE: 'Nepřipojené',
  SPOJKOVISTE_PRIPOJENE: 'Připojené',
  JAMA_NEVYKOPANA: 'Nevykopaná',
  JAMA_VYKOPANA: 'Vykopaná',
  JAMA_ZAKOPANE: 'Finální',
  INFO_INFO: 'Info',
  KONCOVYBOD_NEZAFOUKNUTO: 'Nezafouknuto',
  KONCOVYBOD_ZAFOUKNUTO: 'Zafouknuto',
} as const;

/**
 * Logická fáze → REPREZENTATIVNÍ kód (`WF.PHASE.N`), první varianta daného typu s tímto názvem.
 * Backend porovnává status proti tomuto kódu přes `isSamePhase` (sloučí varianty se stejným názvem),
 * pracuje tedy s kódy, ne s display názvy.
 *
 * Příklad: `PHASE_CODE.BUDOVA_ZAFOUKNUTO` = 'BUDOVA.2.PHASE.2' (matchne i 'BUDOVA.3.PHASE.2').
 */
export const PHASE_CODE: Record<keyof typeof PHASE_NAME, string> = (() => {
  const out = {} as Record<keyof typeof PHASE_NAME, string>;
  /** Sémantické klíče bez odpovídající lineární fáze ve vygenerovaných workflow. */
  const milestoneOnlyKeys = new Set<keyof typeof PHASE_NAME>([
    'BUDOVA_ZRIZENE_VR',
    'ROZVADEC_PROVARENY_PRIVOD',
    'ROZVADEC_ZRIZENO',
  ]);
  /**
   * Výkresy.xlsx still labels the BABOVKA linear phase „Postavená bábovka“.
   * Product copy and `PHASE_NAME` use „Připravená bábovka“; the code must still resolve.
   */
  const workflowPhaseNameAliases: Partial<Record<keyof typeof PHASE_NAME, readonly string[]>> = {
    BUDOVA_POSTAVENA_BABOVKA: ['Postavená bábovka'],
  };
  for (const key of Object.keys(PHASE_NAME) as (keyof typeof PHASE_NAME)[]) {
    if (milestoneOnlyKeys.has(key)) {
      out[key] = '';
      continue;
    }
    const objectType = key.split('_')[0];
    const names = [PHASE_NAME[key], ...(workflowPhaseNameAliases[key] ?? [])];
    const wf = GENERATED_WORKFLOWS.find(
      (w) => w.objectType === objectType && w.phases.some((p) => names.includes(p.name)),
    );
    const phase = wf?.phases.find((p) => names.includes(p.name));
    out[key] = phase?.code ?? '';
  }
  return out;
})();

// =============================================================================
// OBJECT TYPE NORMALIZATION
// =============================================================================

/**
 * Normalizace typu objektu pro workflow lookup (trim + ASCII kanon pro spojkoviště z importů).
 * Kanonické kódy typu (`BUDOVA`, `VYKOP`, …) se v DB/API používají beze změny.
 */
export function normalizeObjectTypeForLookup(objectType: string): string {
  const t = (objectType ?? '').trim();
  if (!t) return '';
  const upper = t.toUpperCase();
  if (upper === 'SPOJKOVIŠTĚ' || upper === 'SPOJKOVISTĚ') return 'SPOJKOVISTE';
  if (upper === 'OBJEKT') return 'BUDOVA';
  if (upper === 'VYKOP_STAVAJICI') return 'STAVAJICI';
  if (upper === 'VYKOP_NOVY' || upper === 'VYKOP_METALIKA') return 'VYKOP';
  return upper;
}

// =============================================================================
// TYPE ALIASY (zpětná kompatibilita — kódy jsou nově libovolné řetězce `WF.PHASE.N`)
// =============================================================================

export type PhaseCode = string;
export type ObjectPhaseCode = string;
